import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertSubstructureBody,
  Substructure,
  UpdateSubstructureNewImageBody,
  UpdateSubstructureRemoveImageBody,
} from "./types/substructures";

export const useSubstructuresApi = () => {
  const api = useApi();

  const getAdminSubstructure = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/substructures/${id}`;
      return useSWRMutation<Substructure, CustomError, string>(url, (url) => api.get<Substructure>({ url, signal }));
    },
    [api],
  );

  const useAdminSubstructures = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId ? `/admin/settings/border/systems/${systemId}/substructures` : "/admin/settings/border/substructures",
        query,
      );
      return useSWRImmutable<Paginated<Substructure>, CustomError, string>(url, (url) =>
        api.get<Paginated<Substructure>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSubstructures = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/substructures", query);
      return api.get<Paginated<Substructure>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminSubstructure = useCallback(
    async (body: InsertSubstructureBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/substructures`;
      const id = await api.post<{ id: string }, InsertSubstructureBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminSubstructure = useCallback(
    async (
      substructureId: string,
      body: UpdateSubstructureRemoveImageBody | UpdateSubstructureNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/border/substructures/${substructureId}`;
      (await api.put<UpdateSubstructureRemoveImageBody | UpdateSubstructureNewImageBody>(url, body),
        mutateCache(["/admin/settings/border/substructures"]));
    },
    [api],
  );

  const updateAdminSubstructureVisibility = useCallback(
    async (substructureId: string, visibility: UpdateVisibilityRequestBody, systemId?: string): Promise<void> => {
      const url = `/admin/settings/border/substructures/${substructureId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([
          `/admin/settings/border/substructures`,
          ...(systemId ? [`/admin/settings/border/systems/${systemId}/substructures`] : []),
        ]));
    },
    [api],
  );

  const updateAdminSubstructureOrder = useCallback(
    async (substructureId: string, order: UpdateOrderRequestBody, systemId?: string): Promise<void> => {
      const url = `/admin/settings/border/substructures/${substructureId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache([
          `/admin/settings/border/substructures`,
          ...(systemId ? [`/admin/settings/border/systems/${systemId}/substructures`] : []),
        ]));
    },
    [api],
  );

  const deleteAdminSubstructure = useCallback(
    async (substructureId: string): Promise<void> => {
      const url = `/admin/settings/border/substructures/${substructureId}`;
      await api.del(url).then(() => {
        mutateCache([`/admin/settings/border/substructures`]);
      });
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (substructureId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/border/substructures/${substructureId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminSubstructure,
    useAdminSubstructures,
    getAdminSubstructures,
    insertAdminSubstructure,
    updateAdminSubstructure,
    updateAdminSubstructureVisibility,
    updateAdminSubstructureOrder,
    deleteAdminSubstructure,
    generateUploadImageUrl,
  };
};
